import { getAuthInstance } from '@zealth/core/firebase/runtime'
import useSignInWithEmailLinkMutation from '@zealth/core/firebase/useSignInWithEmailLinkMutation'
import { isSignInWithEmailLink } from 'firebase/auth'
import { useEffect, useRef, useState } from 'react'

import { clearPendingEmail, readPendingEmail } from './pendingSignInEmail'

export type CompleteEmailLinkSignIn = {
  isRunning: boolean
  error: string | null
}

const wasOpenedFromLink = (): boolean =>
  isSignInWithEmailLink(getAuthInstance(), window.location.href)

/** Finishes a magic-link sign-in when the page was opened from one. */
export const useCompleteEmailLinkSignIn = (): CompleteEmailLinkSignIn => {
  const [isRunning, setIsRunning] = useState(wasOpenedFromLink)
  const [error, setError] = useState<string | null>(null)
  const signIn = useSignInWithEmailLinkMutation()
  const started = useRef(false)

  useEffect(() => {
    if (started.current || !wasOpenedFromLink()) return
    started.current = true

    const email = readPendingEmail()
    if (!email) {
      setError(
        'Open the sign-in link in the same browser you requested it from.'
      )
      setIsRunning(false)
      return
    }

    signIn.mutate(
      { email, emailLink: window.location.href },
      {
        onSuccess: () => {
          clearPendingEmail()
          // Drops the spent one-time code so a reload does not retry it.
          window.history.replaceState({}, '', window.location.pathname)
          setIsRunning(false)
        },
        onError: caught => {
          setError(
            caught instanceof Error ? caught.message : 'That link did not work.'
          )
          setIsRunning(false)
        },
      }
    )
  }, [signIn])

  return { isRunning, error }
}
